import isEqual from 'lodash.isequal'

import {ExceptionType, UsersGroupsOption} from 'interfaces'
import {
  CACAssignment,
  CACAssignments,
  CACRestriction,
  CloudAppAdditionalControls,
  CloudAppListTypes,
  CloudAppResponseType,
  CloudAppSaveApp,
  CloudAppSaveList,
  CloudAppType,
} from 'interfaces/Policy'

import {CAC_GLOBAL_SETTINGS_APP_IDS} from './cloudApp.constant'

export const supportsCacGlobalSettings = (appId: string): boolean =>
  (CAC_GLOBAL_SETTINGS_APP_IDS as readonly string[]).includes(appId)

/** The only way the contract expresses "everyone on the policy". */
export const CAC_ASSIGNMENT_ALL = 'all' as const

export type CacValueAssignment = CACAssignment | typeof CAC_ASSIGNMENT_ALL

export const getCacConfiguredValues = (app?: CloudAppType): string[] =>
  Object.keys(app?.assignments ?? {})

export const getCacValueAssignment = (
  app: CloudAppType | undefined,
  value: string,
): CacValueAssignment => app?.assignments?.[value] ?? CAC_ASSIGNMENT_ALL

export const isAssignedToEveryone = (
  assignment: CacValueAssignment,
): assignment is typeof CAC_ASSIGNMENT_ALL => assignment === CAC_ASSIGNMENT_ALL

/** True only when identities are actually selected, not an empty draft. */
export const cacValueHasRestrictedAccess = (
  assignment: CacValueAssignment,
): boolean =>
  !isAssignedToEveryone(assignment) &&
  (assignment.users.length > 0 || assignment.groups.length > 0)

/**
 * The wire carries bare emails, so `type` is inferred from which list an
 * identity came from and `name` is left for the caller to fall back on.
 */
export const toIdentityOptions = (
  assignment: CacValueAssignment,
): UsersGroupsOption[] => {
  if (isAssignedToEveryone(assignment)) {
    return []
  }

  const toOptions = (emailIds: string[], type: ExceptionType) =>
    Array.from(new Set(emailIds)).map(emailId => ({emailId, name: '', type}))

  return [
    ...toOptions(assignment.users, ExceptionType.User),
    ...toOptions(assignment.groups, ExceptionType.Group),
  ]
}

/** Splits identity options back into the two wire lists, de-duplicated. */
export const toCacAssignment = (
  identities: UsersGroupsOption[],
): CACAssignment => {
  const collect = (type: ExceptionType) =>
    Array.from(
      new Set(
        identities
          .filter(identity => identity.type === type)
          .map(identity => identity.emailId),
      ),
    )

  return {
    users: collect(ExceptionType.User),
    groups: collect(ExceptionType.Group),
  }
}

export const setCacValueAssignment = (
  assignments: CACAssignments | undefined,
  value: string,
  next: CacValueAssignment,
): CACAssignments => ({
  ...(assignments ?? {}),
  [value]: isAssignedToEveryone(next)
    ? CAC_ASSIGNMENT_ALL
    : {
        users: Array.from(new Set(next.users)),
        groups: Array.from(new Set(next.groups)),
      },
})

export const removeCacValueAssignment = (
  assignments: CACAssignments | undefined,
  value: string,
): CACAssignments => {
  if (!assignments?.[value]) {
    return {...(assignments ?? {})}
  }

  const {[value]: _removed, ...rest} = assignments
  return rest
}

/**
 * Rebuilds the map for a new value list, keeping each existing entry and
 * defaulting unseen values to everyone.
 */
export const rebuildCacAssignments = (
  app: CloudAppType | undefined,
  nextValues: string[],
): CACAssignments =>
  Object.fromEntries(
    nextValues.map(value => [
      value,
      app?.assignments?.[value] ?? CAC_ASSIGNMENT_ALL,
    ]),
  )

const isEmptyRestrictedAssignment = (assignment: CacValueAssignment): boolean =>
  !isAssignedToEveryone(assignment) &&
  assignment.users.length === 0 &&
  assignment.groups.length === 0

/**
 * The schema forbids an entry with both lists empty, so a value left
 * restricted with no identities means everyone.
 */
export const normalizeCacAssignments = (
  assignments: CACAssignments | undefined,
): CACAssignments =>
  Object.fromEntries(
    Object.entries(assignments ?? {}).map(([value, assignment]) => [
      value,
      isEmptyRestrictedAssignment(assignment) ? CAC_ASSIGNMENT_ALL : assignment,
    ]),
  )

/** Restores everyone on an app when restricted values still have no identities. */
export const discardEmptyRestrictedAssignments = (
  data: CloudAppResponseType | undefined,
  appId: string,
): CloudAppResponseType | undefined => {
  const app = data?.cac?.[appId]
  if (!data || !app) {
    return data
  }

  const assignments = normalizeCacAssignments(app.assignments)
  if (isEqual(assignments, app.assignments)) {
    return data
  }

  return {
    ...data,
    cac: {
      ...data.cac,
      [appId]: {
        ...app,
        assignments,
      },
    },
  }
}

/** Applies the empty-entry coercion to every app in a CAC config. */
export const normalizeCacData = (cac: CloudAppListTypes): CloudAppListTypes =>
  Object.fromEntries(
    Object.entries(cac).map(([appId, app]) => [
      appId,
      {...app, assignments: normalizeCacAssignments(app.assignments)},
    ]),
  )

/**
 * Empty restricted assignments serialize as everyone, so they are not unsaved
 * work until a user or group is added.
 */
export const hasCloudAppUnsavedChanges = (
  data: CloudAppResponseType | undefined,
  snapshot: CloudAppResponseType | undefined,
): boolean =>
  !isEqual(
    data && {
      ...data,
      cac: serializeCacForSave(data.cac),
    },
    snapshot && {
      ...snapshot,
      cac: serializeCacForSave(snapshot.cac),
    },
  )

type CloudAppWithLegacyValues = CloudAppType & {values?: string[]}

const omitLegacyCacValues = (app: CloudAppWithLegacyValues): CloudAppType => {
  const {values: _dropped, ...rest} = app
  return rest
}

export const serializeAdditionalControls = (
  appId: string,
  controls: CloudAppAdditionalControls | undefined,
): CloudAppAdditionalControls | undefined => {
  if (!controls) return controls
  const {
    consumer_login_allowed,
    consumer_upload_allowed,
    ssh_over_https_allowed,
    gemini_restriction,
    copilot_restriction,
  } = controls

  if (appId === 'github') {
    return {
      consumer_login_allowed,
      consumer_upload_allowed,
      ssh_over_https_allowed,
    }
  }

  const base = {consumer_login_allowed, consumer_upload_allowed}

  if (appId === 'google') {
    return {
      ...base,
      gemini_restriction: gemini_restriction ?? CACRestriction.Allow,
    }
  }
  if (appId === 'o365') {
    return {
      ...base,
      copilot_restriction: copilot_restriction ?? CACRestriction.Allow,
    }
  }
  return base
}

export const serializeCacForSave = (
  cac: CloudAppListTypes | undefined,
): CloudAppSaveList =>
  Object.fromEntries(
    Object.entries(cac ?? {}).map(([appId, app]): [string, CloudAppSaveApp] => {
      const withoutLegacyValues = omitLegacyCacValues(app)
      return [
        appId,
        {
          ...withoutLegacyValues,
          assignments: normalizeCacAssignments(app.assignments),
          ...(withoutLegacyValues.additional_controls
            ? {
                additional_controls:
                  serializeAdditionalControls(
                    appId,
                    withoutLegacyValues.additional_controls,
                  ) ?? withoutLegacyValues.additional_controls,
              }
            : {}),
        },
      ]
    }),
  )
